import { generateHash } from './util';
import { EventDispatcher } from './event-dispatcher';

interface TransportReceiveResponse {
  askerId: string;
  payload?: unknown;
}

interface TransportReceiveAsk {
  name: string;
  askerId: string;
  payload?: unknown;
}

interface TransportEvent {
  name: string;
  payload?: unknown;
}

interface PromissedResponse {
  resolve: (value: unknown | PromiseLike<unknown>) => void;
  reject: (reason?: unknown) => void;
}

class Transport extends EventDispatcher<{
  name: string;
  askerId: string;
  payload?: unknown;
}> {
  private readonly waitingForResponse: Map<string, PromissedResponse> =
    new Map();

  /**
   * Mensagens desconhecidas cairão aqui
   */
  private readonly _trash: MessageEvent[] = [];

  private readonly target: Window;

  private readonly selfWindow: Window;

  /**
   * @param target Pra onde serão enviadas as mensagens [postMessage(...)]
   * @param selfWindow Onde devo escutar as respostas [addEventListener('message', ...)]
   */
  public constructor(target: Window, selfWindow: Window) {
    super();
    this.target = target;
    this.selfWindow = selfWindow;
    this.selfWindow.addEventListener('message', (event) => {
      this.handleEventOrMessageResponse(event);
    });
    this.handleEventOrMessageResponse =
      this.handleEventOrMessageResponse.bind(this);
  }

  private sendMessage(message: {
    name?: string;
    payload?: unknown;
    askerId?: string;
  }) {
    this.target.postMessage(message, this.selfWindow.location.origin);
  }

  public sendResponse(askerId: string, payload?: unknown) {
    this.sendMessage({
      askerId,
      payload,
    });
  }

  public sendEvent(message: { name: string; payload?: unknown }) {
    this.target.postMessage(message, this.selfWindow.location.origin);
  }

  public askAndWaitForResponse(message: {
    name: string;
    payload?: unknown;
  }): Promise<unknown> {
    const id = generateHash().toString();
    this.sendMessage({
      ...message,
      askerId: id,
    });

    return new Promise((resolve, reject) => {
      this.waitingForResponse.set(id, { resolve, reject });
    });
  }

  public onEvent(name: string, callback: (payload?: unknown) => void) {
    this.addListener(name, (data) => {
      callback(data.payload);
    });
  }

  public onAsk(
    name: string,
    callback: (ask: {
      payload?: unknown;
      sendResponse: (payload?: unknown) => void;
    }) => void,
  ) {
    this.addListener(name, (data) => {
      const { askerId, payload } = data;
      const sendResponseProxy = (payload?: unknown) => {
        this.sendResponse(askerId, payload);
      };
      callback({
        payload,
        sendResponse: sendResponseProxy,
      });
    });
  }

  public get trash() {
    return this._trash;
  }

  private handleEventOrMessageResponse(event: MessageEvent) {
    if (this.tryHandleUnsecureEvent(event)) {
      return;
    }

    if (this.tryHandleMessageAsk(event)) {
      return;
    }

    if (this.tryHandleMessageResponse(event)) {
      return;
    }

    if (this.tryHandleEvent(event)) {
      return;
    }

    this.handleTrashEvent(event);
  }

  private tryHandleUnsecureEvent(event: MessageEvent) {
    if (event.origin !== this.selfWindow.location.origin) {
      this.handleTrashEvent(event);
      return true;
    }

    return false;
  }

  private tryHandleMessageAsk(event: MessageEvent) {
    const data = event.data as TransportReceiveAsk;
    const { askerId, name, payload } = data;
    if (typeof askerId === 'string' && typeof name === 'string') {
      this.dispatch({
        name,
        askerId,
        payload,
      });
      return true;
    }
    return false;
  }

  private tryHandleMessageResponse(event: MessageEvent) {
    const data = event.data as TransportReceiveResponse;
    const { askerId, payload } = data;
    if (typeof askerId === 'string') {
      const promissed = this.waitingForResponse.get(askerId);
      if (promissed != null) {
        promissed.resolve(payload);
        this.waitingForResponse.delete(askerId);
        return true;
      }
    }
    return false;
  }

  private tryHandleEvent(event: MessageEvent) {
    const { name, payload } = event.data as TransportEvent;
    if (payload != null && typeof name === 'string') {
      this.dispatch(event.data);
      return true;
    }
    return false;
  }

  private handleTrashEvent(event: MessageEvent) {
    this._trash.push(event);
  }
}

export { Transport };
